import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import PromoBanner from '../components/PromoBanner';
import Footer from '../components/Footer';
import './Pricing.css';

export default function Pricing({ user }) {
  const navigate = useNavigate();
  const t = (key) => getTranslation(user?.language || 'en', key);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  
  // Calculate discounted prices
  const getDiscountedPrice = (originalPrice) => {
    if (couponValid && couponCode.toUpperCase() === 'STORY20') {
      return (originalPrice * 0.8).toFixed(2);
    }
    return originalPrice.toFixed(2);
  };
  
  // Validate coupon when code changes
  const handleCouponChange = (value) => {
    const upperValue = value.toUpperCase();
    setCouponCode(upperValue);
    if (upperValue === 'STORY20') {
      setCouponValid(true);
    } else if (upperValue === '') {
      setCouponValid(null);
    } else {
      setCouponValid(false);
    }
  };

  const handleUpgrade = async (packageId, paymentMethod) => {
    if (!user) {
      navigate('/');
      return;
    }
    
    setLoading(true);
    setSelectedPlan(packageId);
    
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        {
          package_id: packageId,
          payment_method: paymentMethod,
          origin_url: originUrl,
          coupon_code: couponCode || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Redirect to checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.failedCheckoutSession'));
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleBuySingleStory = async (resolution, paymentMethod) => {
    if (!user) {
      navigate('/');
      return;
    }
    
    setLoading(true);
    setSelectedPlan(`story_${resolution}_${paymentMethod}`);
    
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/purchase-story/create-checkout`,
        {
          resolution: resolution,
          payment_method: paymentMethod,
          origin_url: originUrl,
          coupon_code: couponCode || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Redirect to checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const isGerman = user?.language === 'de';

  return (
    <div className="pricing-page">
      <PromoBanner language={user?.language || 'en'} />
      
      <div className="pricing-content">
        <div className="pricing-header">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')}
          >
            {isGerman ? '← Zurück zum Dashboard' : '← Back to Dashboard'}
          </Button>
        </div>

        <div className="pricing-container">
          <div className="pricing-hero">
            <h1>{isGerman ? 'Waehle, wie du weitere Geschichten freischalten willst' : 'Choose how you want to unlock more stories'}</h1>
            <p>{isGerman ? 'Jeder Plan fuehrt zum gleichen Ziel: ein fertiges, illustriertes Kinderbuch mit deinem Kind als Hauptfigur.' : 'Every plan leads to the same outcome: a finished illustrated storybook with your child as the hero.'}</p>
            <div className="pricing-proof-pills">
              <span>{isGerman ? '4 Stories pro Monat im Pro-Plan' : '4 stories per month on Pro'}</span>
              <span>{isGerman ? 'Kind bleibt visuell wiedererkennbar' : 'Child stays visually recognizable'}</span>
              <span>{isGerman ? 'PDF, Druck und Kindle moeglich' : 'PDF, print and Kindle available'}</span>
            </div>
          </div>
          
          {/* Subscription Coupon Code Input */}
          <div className="subscription-coupon-container" style={{maxWidth: '500px', margin: '1.5rem auto 2rem', textAlign: 'center'}}>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '1.1rem'}}>
              {isGerman ? '🎉 Rabattcode für Abonnement (optional)' : '🎉 Subscription Discount Code (optional)'}
            </label>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => handleCouponChange(e.target.value)}
              placeholder={isGerman ? 'z.B. STORY20 für 20% Rabatt' : 'e.g. STORY20 for 20% off'}
              style={{
                width: '100%',
                padding: '0.875rem',
                border: `2px solid ${couponValid === true ? '#10B981' : couponValid === false ? '#EF4444' : '#E5E7EB'}`,
                borderRadius: '10px',
                fontSize: '1.05rem',
                textAlign: 'center',
                textTransform: 'uppercase',
                backgroundColor: couponValid === true ? '#F0FDF4' : couponValid === false ? '#FEF2F2' : 'white',
                fontWeight: '500'
              }}
            />
            {couponValid === true && (
              <div style={{marginTop: '0.75rem', color: '#10B981', fontWeight: '700', fontSize: '0.95rem', padding: '0.5rem', backgroundColor: '#F0FDF4', borderRadius: '8px'}}>
                ✓ {isGerman ? '20% Rabatt für 2 Monate (Monatlich) oder 1 Jahr (Jährlich)!' : '20% discount for 2 months (Monthly) or 1 year (Annual)!'}
              </div>
            )}
            {couponValid === false && (
              <div style={{marginTop: '0.75rem', color: '#EF4444', fontWeight: '600', fontSize: '0.875rem'}}>
                ✗ {isGerman ? 'Ungültiger Code' : 'Invalid code'}
              </div>
            )}
          </div>

        <div className="pricing-tiers">
          {/* Free Tier */}
          <div className="pricing-card free-tier">
            <div className="tier-badge">{isGerman ? 'Kostenlos' : 'Free'}</div>
            <h2>{isGerman ? 'Kostenlos Entdecken' : 'Start Exploring'}</h2>
            <div className="price">
              <span className="amount">€0</span>
              <span className="period">{isGerman ? '/für immer' : '/forever'}</span>
            </div>
            
            <ul className="features-list">
              <li>✓ {isGerman ? '2 Geschichten (lebenslang)' : '2 stories (lifetime)'}</li>
              <li>✓ {isGerman ? 'PDF Export' : 'PDF Export'}</li>
              <li>✓ {isGerman ? 'Premium Illustrationen' : 'Premium illustrations'}</li>
              <li>✓ {isGerman ? 'Haarfarbe & Augenfarbe anpassen' : 'Customize hair & eye color'}</li>
              <li>✓ {isGerman ? 'Kindfoto hochladen' : 'Upload child photo'}</li>
              <li>✓ {isGerman ? 'Englische & Deutsche Sprache' : 'English & German language'}</li>
              <li className="disabled">✗ {isGerman ? 'Geschichten drucken' : 'Print stories'}</li>
              <li className="disabled">✗ {isGerman ? 'An Kindle senden' : 'Send to Kindle'}</li>
            </ul>

            <Button 
              variant="outline" 
              disabled
              className="plan-button"
            >
              {isGerman ? 'Aktueller Plan' : 'Current Plan'}
            </Button>
          </div>

          {/* Pro Tier */}
          <div className="pricing-card pro-tier">
            <div className="tier-badge popular">{isGerman ? 'Beliebteste' : 'Most Popular'}</div>
            <h2>{isGerman ? 'Für Geschichtenerzähler' : 'For Bedtime Storytellers'}</h2>
            <div className="price">
              {couponValid ? (
                <>
                  <span className="amount" style={{textDecoration: 'line-through', color: '#9CA3AF', fontSize: '1.5rem'}}>€5.99</span>
                  <span className="amount" style={{color: '#10B981', marginLeft: '0.5rem'}}>€{getDiscountedPrice(5.99)}</span>
                </>
              ) : (
                <span className="amount">€5.99</span>
              )}
              <span className="period">{isGerman ? '/Monat' : '/month'}</span>
            </div>
            <div className="annual-option">
              {couponValid ? (
                <>
                  {isGerman ? 'oder ' : 'or '}
                  <span style={{textDecoration: 'line-through', color: '#9CA3AF'}}>€59</span>
                  <span style={{color: '#10B981', marginLeft: '0.25rem', fontWeight: '700'}}>€{getDiscountedPrice(59)}</span>
                  {isGerman ? '/Jahr (20% sparen)' : '/year (save 20%)'}
                </>
              ) : (
                isGerman ? 'oder €59/Jahr (20% sparen)' : 'or €59/year (save 20%)'
              )}
            </div>
            
            <ul className="features-list">
              <li>✓ {isGerman ? '4 Geschichten pro Monat' : '4 stories per month'}</li>
              <li>✓ {isGerman ? 'PDF Export' : 'PDF Export'}</li>
              <li>✓ {isGerman ? 'Geschichten drucken' : 'Print stories'}</li>
              <li>✓ {isGerman ? 'An Kindle senden' : 'Send to Kindle'}</li>
              <li>✓ {isGerman ? 'Premium Illustrationen' : 'Premium illustrations'}</li>
              <li>✓ {isGerman ? 'Haarfarbe & Augenfarbe anpassen' : 'Customize hair & eye color'}</li>
              <li>✓ {isGerman ? 'Kindfoto hochladen' : 'Upload child photo'}</li>
              <li>✓ {isGerman ? 'Englische & Deutsche Sprache' : 'English & German language'}</li>
              <li>✓ {isGerman ? 'Prioritäts-Support' : 'Priority support'}</li>
            </ul>

            <div className="payment-options">
              <h3>{isGerman ? 'Wähle deinen Zahlungsplan:' : 'Choose Payment Plan:'}</h3>
              <div className="payment-buttons">
                <Button
                  onClick={() => handleUpgrade('pro_monthly', 'stripe')}
                  disabled={loading}
                  className="plan-button monthly"
                >
                  {loading && selectedPlan === 'pro_monthly' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    `💳 ${isGerman ? 'Monatlich' : 'Monthly'} - €${getDiscountedPrice(5.99)} (Stripe)`
                  )}
                </Button>
                
                <Button
                  onClick={() => handleUpgrade('pro_annual', 'stripe')}
                  disabled={loading}
                  className="plan-button annual"
                >
                  {loading && selectedPlan === 'pro_annual' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    `💳 ${isGerman ? 'Jährlich' : 'Annual'} - €${getDiscountedPrice(59)} (Stripe)`
                  )}
                </Button>

                <div className="payment-divider">{isGerman ? 'oder mit PayPal bezahlen' : 'or pay with PayPal'}</div>

                <Button
                  onClick={() => handleUpgrade('pro_monthly', 'paypal')}
                  disabled={loading}
                  className="plan-button paypal"
                  style={{
                    backgroundColor: '#0070ba',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center'
                  }}
                >
                  {loading && selectedPlan === 'pro_monthly_paypal' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.795.68l-.04.22-.63 3.993-.032.17a.804.804 0 0 1-.794.679H7.72a.483.483 0 0 1-.477-.558L9.718 7.08a.966.966 0 0 1 .952-.813h4.922c.66 0 1.25.044 1.772.144.174.034.34.073.5.118.18.05.352.104.518.166.094.036.186.073.274.114.913.405 1.578 1.13 1.911 2.17.112.355.188.747.236 1.177.024.21.038.43.038.66 0 .068-.003.136-.008.204z"/>
                      </svg>
                      {`${isGerman ? 'Monatlich' : 'Monthly'} - €${getDiscountedPrice(5.99)}`}
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => handleUpgrade('pro_annual', 'paypal')}
                  disabled={loading}
                  className="plan-button paypal"
                  style={{
                    backgroundColor: '#0070ba',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center'
                  }}
                >
                  {loading && selectedPlan === 'pro_annual_paypal' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.795.68l-.04.22-.63 3.993-.032.17a.804.804 0 0 1-.794.679H7.72a.483.483 0 0 1-.477-.558L9.718 7.08a.966.966 0 0 1 .952-.813h4.922c.66 0 1.25.044 1.772.144.174.034.34.073.5.118.18.05.352.104.518.166.094.036.186.073.274.114.913.405 1.578 1.13 1.911 2.17.112.355.188.747.236 1.177.024.21.038.43.038.66 0 .068-.003.136-.008.204z"/>
                      </svg>
                      {`${isGerman ? 'Jährlich' : 'Annual'} - €${getDiscountedPrice(59)}`}
                    </>
                  )}
                </Button>
              </div>
              
              {/* Recurring Payment Disclaimer */}
              <div className="subscription-disclaimer">
                <p className="disclaimer-text">
                  {isGerman ? (
                    <>
                      ℹ️ Das Pro-Abonnement verlängert sich automatisch monatlich/jährlich, bis Sie es kündigen. 
                      Sie können jederzeit in den Einstellungen kündigen. Nach der Kündigung haben Sie bis zum Ende 
                      des bezahlten Zeitraums Zugriff auf Pro-Funktionen.
                    </>
                  ) : (
                    <>
                      ℹ️ Pro subscription automatically renews monthly/annually until you cancel. 
                      You can cancel anytime in Settings. After cancellation, you'll have access to Pro features 
                      until the end of your paid period.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Single Story Purchase Section */}
        <div id="single-stories" className="single-story-section">
          <h2>{isGerman ? 'Einzelne Geschichten kaufen' : 'Buy Single Stories'}</h2>
          <p>{isGerman ? 'Einmalige Zahlung - Kein Abonnement!' : 'One-time payment - No subscription!'}</p>
          <p style={{fontSize: '0.95rem', color: '#6B7280'}}>{isGerman ? 'Perfekt für beide - Gratis & Pro Nutzer!' : 'Perfect for both Free & Pro users!'}</p>
          
          {/* Coupon Code Input */}
          <div className="coupon-input-container" style={{maxWidth: '400px', margin: '1.5rem auto', textAlign: 'center'}}>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
              {isGerman ? 'Rabattcode (optional)' : 'Discount Code (optional)'}
            </label>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => handleCouponChange(e.target.value)}
              placeholder={isGerman ? 'z.B. STORY20' : 'e.g. STORY20'}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `2px solid ${couponValid === true ? '#10B981' : couponValid === false ? '#EF4444' : '#E5E7EB'}`,
                borderRadius: '8px',
                fontSize: '1rem',
                textAlign: 'center',
                textTransform: 'uppercase',
                backgroundColor: couponValid === true ? '#F0FDF4' : couponValid === false ? '#FEF2F2' : 'white'
              }}
            />
            {couponValid === true && (
              <div style={{marginTop: '0.5rem', color: '#10B981', fontWeight: '600', fontSize: '0.875rem'}}>
                ✓ {isGerman ? '20% Rabatt wird angewendet!' : '20% discount will be applied!'}
              </div>
            )}
            {couponValid === false && (
              <div style={{marginTop: '0.5rem', color: '#EF4444', fontWeight: '600', fontSize: '0.875rem'}}>
                ✗ {isGerman ? 'Ungültiger Code' : 'Invalid code'}
              </div>
            )}
          </div>
          
          <div className="story-options">
            {/* 512x512 Story */}
            <div className="story-option-card">
              <h3>{isGerman ? 'Standard Geschichte' : 'Standard Story'}</h3>
              <div className="story-price">
                {couponValid ? (
                  <>
                    <span className="amount" style={{textDecoration: 'line-through', color: '#9CA3AF', fontSize: '1.25rem'}}>€2.99</span>
                    <span className="amount" style={{color: '#10B981', marginLeft: '0.5rem'}}>€{getDiscountedPrice(2.99)}</span>
                  </>
                ) : (
                  <span className="amount">€2.99</span>
                )}
              </div>
              <ul className="features-list">
                <li>✓ {isGerman ? '1 Geschichte Credit' : '1 story credit'}</li>
                <li>✓ {isGerman ? 'Premium Illustrationen' : 'Premium illustrations'}</li>
                <li>✓ {isGerman ? 'Alle Funktionen enthalten' : 'All features included'}</li>
                <li>✓ {isGerman ? 'Keine Verpflichtung' : 'No commitment'}</li>
              </ul>
              
              <div className="payment-buttons-story">
                <Button
                  onClick={() => handleBuySingleStory('512x512', 'stripe')}
                  disabled={loading}
                  className="plan-button single"
                >
                  {loading && selectedPlan === 'story_512x512_stripe' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    `💳 Stripe - €${getDiscountedPrice(2.99)}`
                  )}
                </Button>
                
                <Button
                  onClick={() => handleBuySingleStory('512x512', 'paypal')}
                  disabled={loading}
                  className="plan-button paypal"
                  style={{
                    backgroundColor: '#0070ba',
                    color: 'white'
                  }}
                >
                  {loading && selectedPlan === 'story_512x512_paypal' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{marginRight: '8px'}}>
                        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.795.68l-.04.22-.63 3.993-.032.17a.804.804 0 0 1-.794.679H7.72a.483.483 0 0 1-.477-.558L9.718 7.08a.966.966 0 0 1 .952-.813h4.922c.66 0 1.25.044 1.772.144.174.034.34.073.5.118.18.05.352.104.518.166.094.036.186.073.274.114.913.405 1.578 1.13 1.911 2.17.112.355.188.747.236 1.177.024.21.038.43.038.66 0 .068-.003.136-.008.204z"/>
                      </svg>
                      {`PayPal - €${getDiscountedPrice(2.99)}`}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* 1024x1024 Story */}
            <div className="story-option-card premium">
              <div className="tier-badge extra">{isGerman ? 'Premium' : 'Premium'}</div>
              <h3>{isGerman ? 'Premium Geschichte' : 'Premium Story'}</h3>
              <div className="story-price">
                {couponValid ? (
                  <>
                    <span className="amount" style={{textDecoration: 'line-through', color: '#9CA3AF', fontSize: '1.25rem'}}>€3.99</span>
                    <span className="amount" style={{color: '#10B981', marginLeft: '0.5rem'}}>€{getDiscountedPrice(3.99)}</span>
                  </>
                ) : (
                  <span className="amount">€3.99</span>
                )}
              </div>
              <ul className="features-list">
                <li>✓ {isGerman ? '1 Geschichte Credit' : '1 story credit'}</li>
                <li>✓ {isGerman ? 'Premium Illustrationen - Hohe Qualität' : 'Premium illustrations - High quality'}</li>
                <li>✓ {isGerman ? 'Höhere Bildqualität' : 'Higher image quality'}</li>
                <li>✓ {isGerman ? 'Alle Funktionen enthalten' : 'All features included'}</li>
                <li>✓ {isGerman ? 'Keine Verpflichtung' : 'No commitment'}</li>
              </ul>
              
              <div className="payment-buttons-story">
                <Button
                  onClick={() => handleBuySingleStory('1024x1024', 'stripe')}
                  disabled={loading}
                  className="plan-button single premium"
                >
                  {loading && selectedPlan === 'story_1024x1024_stripe' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    `💳 Stripe - €${getDiscountedPrice(3.99)}`
                  )}
                </Button>
                
                <Button
                  onClick={() => handleBuySingleStory('1024x1024', 'paypal')}
                  disabled={loading}
                  className="plan-button paypal premium"
                  style={{
                    backgroundColor: '#0070ba',
                    color: 'white'
                  }}
                >
                  {loading && selectedPlan === 'story_1024x1024_paypal' ? (
                    <>
                      <span className="spinner"></span>
                      {isGerman ? 'Verarbeitung...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{marginRight: '8px'}}>
                        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.795.68l-.04.22-.63 3.993-.032.17a.804.804 0 0 1-.794.679H7.72a.483.483 0 0 1-.477-.558L9.718 7.08a.966.966 0 0 1 .952-.813h4.922c.66 0 1.25.044 1.772.144.174.034.34.073.5.118.18.05.352.104.518.166.094.036.186.073.274.114.913.405 1.578 1.13 1.911 2.17.112.355.188.747.236 1.177.024.21.038.43.038.66 0 .068-.003.136-.008.204z"/>
                      </svg>
                      {`PayPal - €${getDiscountedPrice(3.99)}`}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      <Footer language={user?.language || 'en'} />
    </div>
  );
}
